import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { CSSObjectWithLabel, OptionProps, ControlProps } from 'react-select';

import { SelectOption } from './types';

const bodyFont = {
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  fontFamily: 'Jost, sans-serif',
};

interface Props {
  error?: string;
  maxHeight?: number;
}

export const useSelectStyles = (props: Props) => {
  const theme = useTheme();

  const { error, maxHeight } = props;

  const styles = useMemo(() => {
    return {
      container: (css: CSSObjectWithLabel) => ({
        ...css,
      }),

      control: (css: CSSObjectWithLabel, state: ControlProps<any>) => {
        const { menuIsOpen, isFocused } = state;

        const borderStyle = () => {
          const borderColor =
            isFocused || menuIsOpen
              ? theme.colors.white.main
              : error
              ? theme.colors.red.main
              : theme.colors.grey.main;

          return {
            borderTop: `1px solid ${borderColor}`,
            borderLeft: `1px solid ${borderColor}`,
            borderRight: `1px solid ${borderColor}`,
            borderBottom: menuIsOpen
              ? `1px solid transparent`
              : `1px solid ${borderColor}`,

            borderTopLeftRadius: theme.borderRadius.default,
            borderTopRightRadius: theme.borderRadius.default,
            borderBottomLeftRadius: menuIsOpen ? 0 : theme.borderRadius.default,
            borderBottomRightRadius: menuIsOpen
              ? 0
              : theme.borderRadius.default,
          };
        };

        return {
          ...css,
          ...borderStyle(),

          width: '100%',
          height: '48px',
          boxShadow: 'none',
          cursor: 'pointer',
          backgroundColor: menuIsOpen
            ? theme.colors.grey.active
            : theme.colors.grey.main,

          '&:hover': {
            backgroundColor: theme.colors.grey.hover,
          },

          '& svg': {
            '& path': {
              fill: error ? theme.colors.red.main : theme.colors.white.main,
            },
            transform: state.menuIsOpen ? 'rotate(180deg)' : 'rotate(0)',
          },
        };
      },

      input: (css: CSSObjectWithLabel) => {
        return {
          ...css,
          color: theme.colors.white.main,
          ...bodyFont,
        };
      },

      placeholder: (css: CSSObjectWithLabel) => ({
        ...css,
        ...bodyFont,
        color: error ? theme.colors.red.main : theme.colors.lightGrey.main,
      }),

      indicatorSeparator: (css: CSSObjectWithLabel) => {
        return { ...css, display: 'none' };
      },

      menu: (css: CSSObjectWithLabel) => ({
        ...css,
        marginTop: -1,
        padding: 0,
        zIndex: 2,
        backgroundColor: theme.colors.grey.active,
        boxShadow: 'none',
        overflow: 'hidden',

        borderTop: 'none',
        borderLeft: `1px solid ${theme.colors.white.main}`,
        borderRight: `1px solid ${theme.colors.white.main}`,
        borderBottom: `1px solid ${theme.colors.white.main}`,

        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: theme.borderRadius.default,
        borderBottomRightRadius: theme.borderRadius.default,
      }),

      menuList: (css: CSSObjectWithLabel) => ({
        ...css,
        padding: 0,
        maxHeight: maxHeight,
      }),

      option: (css: CSSObjectWithLabel, state: OptionProps<SelectOption>) => {
        const { isSelected } = state;

        return {
          ...css,
          ...bodyFont,
          minHeight: '48px',
          cursor: 'pointer',
          padding: `12px ${theme.padding.input}`,

          color: isSelected
            ? theme.colors.lightGrey.main
            : theme.colors.white.main,
          backgroundColor: theme.colors.grey.active,

          ':hover': {
            backgroundColor: 'rgba(108, 109, 106, 0.3)',
            color: theme.colors.white.main,
          },
        };
      },

      valueContainer: (css: CSSObjectWithLabel) => ({
        ...css,
        padding: `0 ${theme.padding.input}`,
      }),

      singleValue: (css: CSSObjectWithLabel) => ({
        ...css,
        ...bodyFont,
        marginRight: 0,
        marginLeft: 0,
        color: theme.colors.white.main,
      }),

      noOptionsMessage: (css: CSSObjectWithLabel) => ({
        ...css,
        ...bodyFont,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        height: '48px',
        color: theme.colors.lightGrey.main,
        backgroundColor: theme.colors.grey.active,
      }),
    };
  }, [error, theme, maxHeight]);

  return styles;
};
